import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

const DEFAULT_LINE_HEIGHT = 20;
const DEFAULT_OVERSCAN = 10;

interface VirtualLogListProps {
  lines: string[];
  lineHeight?: number;
  overscan?: number;
}

export const VirtualLogList: React.FC<VirtualLogListProps> = ({
  lines,
  lineHeight = DEFAULT_LINE_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setViewportHeight(el.clientHeight);
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const totalHeight = lines.length * lineHeight;

  const { startIndex, visibleLines } = useMemo(() => {
    if (viewportHeight <= 0 || lines.length === 0) {
      return { startIndex: 0, visibleLines: [] as string[] };
    }
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / lineHeight) - overscan,
    );
    const endIndex = Math.min(
      lines.length,
      Math.ceil((scrollTop + viewportHeight) / lineHeight) + overscan,
    );
    return {
      startIndex,
      visibleLines: lines.slice(startIndex, endIndex),
    };
  }, [scrollTop, viewportHeight, lines, lineHeight, overscan]);

  return (
    <div
      ref={containerRef}
      className="log-content log-virtual"
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: totalHeight, position: "relative" }}>
        {visibleLines.map((line, i) => {
          const index = startIndex + i;
          return (
            <div
              key={index}
              className="log-line"
              style={{ top: index * lineHeight }}
            >
              {line}
            </div>
          );
        })}
      </div>
    </div>
  );
};
